import React, { useEffect, useState } from 'react';
import { Link, useLocation, Outlet, useNavigate } from 'react-router-dom';
import {
  Squares2X2Icon,
  UsersIcon,
  CreditCardIcon,
  MagnifyingGlassIcon,
  ArrowRightOnRectangleIcon,
  InboxStackIcon,
  ChatBubbleLeftEllipsisIcon,
  ServerStackIcon,
  CogIcon,
  KeyIcon,
  Bars3Icon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import apiClient from '../../lib/apiClient';
import { useAuthStore } from '../../store/useAuthStore';
import logo from '../../assets/logo.png';

const NAV_MAIN = [
  { name: 'Dashboard',         path: '/',                 icon: Squares2X2Icon },
  { name: 'User Management',   path: '/users',            icon: UsersIcon },
  { name: 'Subscriptions',     path: '/subscriptions',    icon: CreditCardIcon },
  { name: 'Coupons',           path: '/coupons',          icon: CreditCardIcon },
  { name: 'Serper Keys',      path: '/serper-keys',      icon: KeyIcon },
  // { name: 'Sales & Enquiries', path: '/sales-enquiries',  icon: BriefcaseIcon },
  { name: 'Support Tickets',   path: '/support-tickets',  icon: ChatBubbleLeftEllipsisIcon },
  { name: 'System Health',     path: '/system-health',    icon: ServerStackIcon },
];

const NAV_REPORTS = [
  { name: 'Search History', path: '/searches',  icon: MagnifyingGlassIcon },
  { name: 'Payments',       path: '/payments',  icon: InboxStackIcon },
];

function isActive(navPath: string, currentPath: string) {
  if (navPath === '/') return currentPath === '/' || currentPath === '';
  return currentPath === navPath || currentPath.startsWith(navPath + '/');
}

interface AdminProfile {
  name: string;
  email: string;
  avatar_url: string | null;
}

export default function AdminLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const [adminProfile, setAdminProfile] = useState<AdminProfile | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    apiClient.get('/admin/profile')
      .then(({ data }) => setAdminProfile(data))
      .catch(() => {});
  }, []);

  // Close sidebar on route change (mobile)
  useEffect(() => { setSidebarOpen(false); }, [location.pathname]);

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    useAuthStore.getState().clearAuth();
    navigate('/login');
  };

  const initials = adminProfile
    ? adminProfile.name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()
    : 'AD';

  const sidebarContent = (
    <>
      {/* Logo */}
      <div className="h-[64px] flex items-center justify-between px-5 border-b border-slate-100 shrink-0">
        <img src={logo} alt="Localcontact" className="h-10 w-auto object-contain" />
        <button
          onClick={() => setSidebarOpen(false)}
          className="md:hidden w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:bg-slate-100 transition-colors"
        >
          <XMarkIcon className="w-5 h-5" />
        </button>
      </div>

      {/* Main Nav */}
      <nav className="flex-1 px-3 py-4 overflow-y-auto">
        <p className="text-[9px] font-black text-slate-400 uppercase tracking-[0.2em] px-3 mb-2">Main Menu</p>
        <div className="space-y-0.5">
          {NAV_MAIN.map(item => {
            const active = isActive(item.path, location.pathname);
            return (
              <Link
                key={item.name}
                to={item.path}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-[13px] font-semibold transition-all ${
                  active
                    ? 'bg-brand-50 text-brand-700 font-bold'
                    : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                }`}
              >
                <item.icon className={`w-[17px] h-[17px] shrink-0 ${active ? 'text-brand-500' : 'text-slate-400'}`} />
                {item.name}
              </Link>
            );
          })}
        </div>

        <p className="text-[9px] font-black text-slate-400 uppercase tracking-[0.2em] px-3 mb-2 mt-5">Reports</p>
        <div className="space-y-0.5">
          {NAV_REPORTS.map(item => {
            const active = isActive(item.path, location.pathname);
            return (
              <Link
                key={item.name}
                to={item.path}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-[13px] font-semibold transition-all ${
                  active
                    ? 'bg-brand-50 text-brand-700 font-bold'
                    : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                }`}
              >
                <item.icon className={`w-[17px] h-[17px] shrink-0 ${active ? 'text-brand-500' : 'text-slate-400'}`} />
                {item.name}
              </Link>
            );
          })}
        </div>
      </nav>

      {/* Bottom */}
      <div className="px-3 pb-3 border-t border-slate-100 pt-3 space-y-0.5">
        <Link to="/settings" className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-[13px] font-semibold text-slate-500 hover:bg-slate-50 hover:text-slate-800 transition-all">
          <CogIcon className="w-[17px] h-[17px] shrink-0 text-slate-400" />
          Settings
        </Link>
        <button
          onClick={handleLogout}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-[13px] font-semibold text-slate-500 hover:bg-red-50 hover:text-red-600 transition-all"
        >
          <ArrowRightOnRectangleIcon className="w-[17px] h-[17px] shrink-0" />
          Sign out
        </button>
        <div className="flex items-center gap-2.5 px-3 py-2 mt-1 rounded-lg bg-emerald-50 border border-emerald-100">
          <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse shrink-0" />
          <div>
            <p className="text-[9px] font-black text-emerald-600 uppercase tracking-widest">System Status</p>
            <p className="text-[11px] font-bold text-emerald-700">Active & Stable</p>
          </div>
        </div>
      </div>
    </>
  );

  return (
    <div className="flex h-screen bg-slate-50 font-sans overflow-hidden">

      {/* ── Mobile backdrop ─────────────────────────────────────────────── */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-40 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* ── Sidebar — desktop always visible, mobile slide-in ───────────── */}
      <aside className="hidden md:flex w-[220px] bg-white border-r border-slate-200 flex-col shrink-0">
        {sidebarContent}
      </aside>

      {/* Mobile sidebar */}
      <aside className={`md:hidden fixed inset-y-0 left-0 z-50 w-[240px] bg-white border-r border-slate-200 flex flex-col transform transition-transform duration-200 ease-in-out ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        {sidebarContent}
      </aside>

      {/* ── Main Content ────────────────────────────────────────────────── */}
      <div className="flex-1 flex flex-col h-screen overflow-hidden min-w-0">

        {/* Top Header */}
        <header className="h-[64px] bg-white border-b border-slate-200 flex items-center justify-between px-4 sm:px-6 shrink-0 gap-4">
          <button
            onClick={() => setSidebarOpen(true)}
            className="md:hidden w-8 h-8 flex items-center justify-center rounded-lg text-slate-500 hover:bg-slate-100 transition-colors shrink-0"
          >
            <Bars3Icon className="w-5 h-5" />
          </button>

          <div className="flex-1" />

          <div className="flex items-center gap-2.5">
            <Link
              to="/settings"
              className="flex items-center gap-2 pl-2.5 border-l border-slate-200 ml-1 hover:opacity-80 transition-opacity"
            >
              <div className="w-8 h-8 rounded-full overflow-hidden bg-brand-500 flex items-center justify-center shrink-0">
                {adminProfile?.avatar_url ? (
                  <img src={adminProfile.avatar_url} alt="avatar" className="w-full h-full object-cover" />
                ) : (
                  <span className="text-[10px] font-black text-white">{initials}</span>
                )}
              </div>
              <div className="hidden sm:block">
                <p className="text-[12px] font-bold text-slate-800 leading-tight">
                  {adminProfile?.name ?? 'Admin'}
                </p>
                <p className="text-[10px] text-slate-400 leading-tight">Administrator</p>
              </div>
            </Link>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto bg-slate-50">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
